import { useEffect, useRef } from 'react';
import {
  formatDateForChartAPI,
  getDateString,
  parseDateString,
  validateTargetLaunchDate,
} from '../bidAnalysisTemplate.logic';

export interface UseCommonEffectsDeps {
  formData: any;
  setFormData: (value: any | ((prev: any) => any)) => void;
  packSizeFormData: any;
  setPackSizeFormData: (value: any | ((prev: any) => any)) => void;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  bidId: string | undefined;
  bidDetails: any;
  BidAnalysisStore: any;
  chartDateRange: { startDate: Date | null; endDate: Date | null };
  setTempDateRange: (value: any) => void;
  tempDateRange: { startDate: Date | null; endDate: Date | null };
  loadBidDetails: () => void | Promise<any>;
  loadNotes: () => void;
  loadTemplateData: () => void;
  loadMarketSalesTrendData: (company?: string | null, skipStore?: boolean, startDate?: Date, endDate?: Date) => void;
  loadMarketShareByVolume: (skipStore?: boolean, startDate?: Date, endDate?: Date) => void;
  loadVolumeByCot: (labeller?: string | null, customStartDate?: Date | string | null, customEndDate?: Date | string | null) => Promise<void>;
  loadContractPriceRanking: (startDate?: string, endDate?: string, skipStore?: boolean) => void;
  setChartDateRange: (value: any) => void;
  setCalendarViewDates: (value: any) => void;
  recalculateFields: (data: any, triggerField?: string) => any;
  preserveGlobalFields: (newData: any) => any;
  setValidationErrors: (value: any | ((prev: any) => any)) => void;
  validateField: (fieldName: string, value: any) => string | null;
  setNdcValidated: (value: boolean) => void;
  ndcValidated: boolean;
  setYearFormData: (value: any | ((prev: any) => any)) => void;
  setGsmsHndlgType: (value: string) => void;
  gsmsHndlgTypeManuallyChangedRef: React.MutableRefObject<boolean>;
  gsmsHndlgType: string;
  setNdcOptions: (value: any[]) => void;
  loadNdcOptions: (supplier: string, concept: string, packSize: number) => void;
  selectedConcept: string | null;
  supplierName: string | null;
  handlingTypesList: any[];
  contractsList: any[];
  contractsExplicitlyClearedRef: React.MutableRefObject<boolean>;
  setisNavigationRestricted: (value: boolean) => void;
  setIsNavigatingAway: (value: boolean) => void;
  location: { pathname: string };
  isNavigatingAway: boolean;
  isManagerReviewMode: boolean;
  setPendingNavigation: (path: string | null) => void;
  setIsBackNavigation: (value: boolean) => void;
  isApplyingFilterRef: React.MutableRefObject<boolean>;
  loading: boolean;
  hasUserMadeChangesRef: React.MutableRefObject<boolean>;
  isInitialLoadRef: React.MutableRefObject<boolean>;
  ndcNotFoundShownRef: React.MutableRefObject<boolean>;
  yearFormData: any;
  yearFormDataRef: React.MutableRefObject<any>;
  selectedCompany: string | null;
  analysisType: string;
  contractPriceRanking: any[];
  showChartModal: boolean;
}

export function useCommonEffects(deps: UseCommonEffectsDeps): void {
  const {
    formData,
    setFormData,
    packSizeFormData,
    setPackSizeFormData,
    activePackSizeTab,
    activeYearTab,
    bidId,
    bidDetails,
    BidAnalysisStore,
    chartDateRange,
    setTempDateRange,
    tempDateRange,
    loadBidDetails,
    loadNotes,
    loadTemplateData,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    loadVolumeByCot,
    loadContractPriceRanking,
    setChartDateRange,
    setCalendarViewDates,
    recalculateFields,
    preserveGlobalFields,
    setValidationErrors,
    validateField,
    setNdcValidated,
    ndcValidated,
    setYearFormData,
    setGsmsHndlgType,
    gsmsHndlgTypeManuallyChangedRef,
    gsmsHndlgType,
    setNdcOptions,
    loadNdcOptions,
    selectedConcept,
    supplierName,
    handlingTypesList,
    contractsList,
    contractsExplicitlyClearedRef,
    setisNavigationRestricted,
    setIsNavigatingAway,
    location,
    isNavigatingAway,
    isManagerReviewMode,
    setPendingNavigation,
    setIsBackNavigation,
    isApplyingFilterRef,
    loading,
    hasUserMadeChangesRef,
    isInitialLoadRef,
    ndcNotFoundShownRef,
    yearFormData,
    yearFormDataRef,
    selectedCompany,
    analysisType,
    contractPriceRanking,
    showChartModal,
  } = deps;

  // Use null (not undefined) as sentinel so the guard doesn't short-circuit when bidId is also
  // undefined (no-bidId route). undefined === undefined would skip the first load entirely.
  const initialLoadBidIdRef = useRef<string | null | undefined>(null);
  const prevTabRef = useRef<number | null>(null);
  const prevCompanyRef = useRef<string | null>(null);
  // Tracks the key of the last market-data load triggered by the primary concept/packSize effect,
  // so the secondary usagePeriod effect can skip a redundant duplicate call on initial load.
  const lastMarketLoadKeyRef = useRef<string | null>(null);

  // Sync tempDateRange with chartDateRange when chartDateRange changes from external sources
  const chartStartTime = chartDateRange.startDate?.getTime();
  const chartEndTime = chartDateRange.endDate?.getTime();
  useEffect(() => {
    if (!isApplyingFilterRef.current) {
      const currentData = (packSizeFormData as any)[activePackSizeTab as any] || formData;
      const bidIdNum = bidId ? parseInt(bidId) : null;
      const concept = currentData.conceptName;
      const packSize = currentData.packSize ? parseFloat(currentData.packSize) : null;
      const hasCustomDates =
        bidIdNum && concept && packSize
          ? BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize) !== undefined
          : false;
      if (!hasCustomDates) {
        const tempStartTime = tempDateRange.startDate?.getTime();
        const tempEndTime = tempDateRange.endDate?.getTime();
        if (tempStartTime !== chartStartTime || tempEndTime !== chartEndTime) {
          setTempDateRange({ ...chartDateRange });
        }
      }
    }
    isApplyingFilterRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartStartTime,
    chartEndTime,
    bidId,
    formData.conceptName,
    formData.packSize,
    activePackSizeTab,
  ]);

  // Guard: run initial load only once per bidId to prevent infinite loops (e.g. from remounts or strict mode)
  useEffect(() => {
    const prev = initialLoadBidIdRef.current;
    if (prev === bidId) return;
    initialLoadBidIdRef.current = bidId;
    ndcNotFoundShownRef.current = false;
    if (bidId) {
      loadBidDetails();
      loadNotes();
    } else {
      loadTemplateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  // Sync formData when active tab changes (MAS - skip NC)
  const currentTemplateType = bidDetails?.template_type || 'mas';
  useEffect(() => {
    if (currentTemplateType === 'nc') {
      return;
    }

    const currentTabData = (packSizeFormData as any)[activePackSizeTab as any];

    if (currentTabData) {
      const syncedData = {
        ...currentTabData,
        contracts: formData.contracts,
        contractsData: formData.contractsData,
        supplierName: formData.supplierName || currentTabData.supplierName,
        gsmsHndlgType: gsmsHndlgType || currentTabData.gsmsHndlgType,
        analysisDate: formData.analysisDate,
        targetLaunchDate: formData.targetLaunchDate,
        analysisType: formData.analysisType,
      };

      setFormData(syncedData);
      if (currentTabData.ndcValidated === true) {
        setNdcValidated(true);
      } else {
        setNdcValidated(false);
      }

      const _tabChanged = prevTabRef.current !== null && prevTabRef.current !== activePackSizeTab;
      const companyChanged =
        prevCompanyRef.current !== null && prevCompanyRef.current !== selectedCompany;
      const isInitialMount = prevTabRef.current === null;

      if (
        companyChanged &&
        !isInitialMount &&
        !isApplyingFilterRef.current &&
        currentTabData.conceptName &&
        currentTabData.packSize
      ) {
        const bidIdNum = bidId ? parseInt(bidId) : null;
        const concept = currentTabData.conceptName;
        const packSize = currentTabData.packSize ? parseFloat(currentTabData.packSize) : null;

        let datesToUse: { startDate: Date | string | null; endDate: Date | string | null } | null =
          null;

        if (bidIdNum && concept && packSize) {
          const storedDateRange = BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize);
          if (
            storedDateRange !== undefined &&
            storedDateRange.startDate &&
            storedDateRange.endDate
          ) {
            datesToUse = {
              startDate: storedDateRange.startDate,
              endDate: storedDateRange.endDate,
            };
          }
        }

        if (!datesToUse) {
          const startDate = currentTabData.usagePeriodStartDate;
          const endDate = currentTabData.usagePeriodEndDate;
          if (startDate && endDate) {
            datesToUse = { startDate, endDate };
          }
        }

        if (datesToUse && datesToUse.startDate && datesToUse.endDate) {
          const parsedStartDate =
            datesToUse.startDate instanceof Date
              ? datesToUse.startDate
              : typeof datesToUse.startDate === 'string'
                ? new Date(datesToUse.startDate)
                : null;
          const parsedEndDate =
            datesToUse.endDate instanceof Date
              ? datesToUse.endDate
              : typeof datesToUse.endDate === 'string'
                ? new Date(datesToUse.endDate)
                : null;

          if (
            parsedStartDate &&
            parsedEndDate &&
            !isNaN(parsedStartDate.getTime()) &&
            !isNaN(parsedEndDate.getTime())
          ) {
            loadMarketSalesTrendData(selectedCompany, false, parsedStartDate, parsedEndDate);
            if (_tabChanged) loadMarketShareByVolume(false, parsedStartDate, parsedEndDate);
            loadVolumeByCot(selectedCompany, parsedStartDate, parsedEndDate);
          } else {
            loadMarketSalesTrendData(selectedCompany, false);
            if (_tabChanged) loadMarketShareByVolume(false);
            loadVolumeByCot(selectedCompany);
          }
        } else {
          loadMarketSalesTrendData(selectedCompany, false);
          if (_tabChanged) loadMarketShareByVolume(false);
          loadVolumeByCot(selectedCompany);
        }
      }
    }

    prevTabRef.current = activePackSizeTab;
    prevCompanyRef.current = selectedCompany;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePackSizeTab, selectedCompany]);

  // Sync validation state based on formData NDC fields
  useEffect(() => {
    const currentData = (packSizeFormData as any)[activePackSizeTab as any] || formData;
    const hasNdcFields = !!(
      currentData.gsms_mfg_ndc ||
      currentData.mfg_ndc ||
      currentData.selectedMfgNdc
    );

    if (!hasNdcFields && ndcValidated) {
      setNdcValidated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.gsms_mfg_ndc,
    formData.mfg_ndc,
    formData.selectedMfgNdc,
    packSizeFormData,
    activePackSizeTab,
    ndcValidated,
  ]);

  // Fetch supplier discount for Direct products and NC templates
  useEffect(() => {
    const productType = bidDetails?.product_type ?? null;

    const fetchSupplierDiscount = async () => {
      const currentTemplateType = bidDetails?.template_type || 'mas';
      const isNC = currentTemplateType === 'nc';
      const isDirect = productType === 'direct';
      const isIndirect = productType === 'indirect';

      if (isIndirect || (!isDirect && !isNC)) {
        return;
      }

      const currentPackData =
        isNC && activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5
          ? (yearFormDataRef.current as any)[activePackSizeTab]?.[activeYearTab] ||
            (yearFormData as any)[activePackSizeTab]?.[activeYearTab] ||
            formData
          : (packSizeFormData as any)[activePackSizeTab as any] || formData;

      const validatedNdc =
        currentPackData.gsms_mfg_ndc || currentPackData.selectedMfgNdc || currentPackData.mfg_ndc;
      const supplierCode =
        validatedNdc && validatedNdc.length >= 5 ? validatedNdc.substring(0, 5) : '00000';

      try {
        const totalDiscount = await BidAnalysisStore.fetchSupplierDiscount(supplierCode);
        const discountToStore =
          totalDiscount !== null && totalDiscount !== undefined ? totalDiscount : 0;

        if (isNC && activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5) {
          setYearFormData(prev => ({
            ...prev,
            [activePackSizeTab as number]: {
              ...prev[activePackSizeTab as number],
              [activeYearTab]: {
                ...prev[activePackSizeTab as number]?.[activeYearTab],
                supplierDiscount: discountToStore,
                supplierCode: supplierCode,
              },
            },
          }));
        } else {
          setPackSizeFormData(prev => ({
            ...prev,
            [activePackSizeTab as number]: {
              ...prev[activePackSizeTab as number],
              supplierDiscount: discountToStore,
              supplierCode: supplierCode,
            },
          }));
        }

        const updatedData = {
          ...formData,
          ...currentPackData,
          supplierDiscount: discountToStore,
          supplierCode: supplierCode,
        };

        const recalculatedData = recalculateFields(updatedData, 'gsmsCostFromMfg');

        const wasProfitManuallySet =
          updatedData._gsmsProfitManuallySet === true ||
          (updatedData.gsmsProfit != null &&
            updatedData.gsmsProfit !== '' &&
            parseFloat(String(updatedData.gsmsProfit)) !== 0);
        let finalRecalculatedData =
          wasProfitManuallySet && updatedData.gsmsProfit != null && updatedData.gsmsProfit !== ''
            ? {
                ...recalculatedData,
                gsmsProfit: updatedData.gsmsProfit,
                _gsmsProfitManuallySet: true,
              }
            : recalculatedData;

        if (isNC && currentPackData.blendedWhlsFee != null && currentPackData.blendedWhlsFee !== '') {
          finalRecalculatedData = {
            ...finalRecalculatedData,
            blendedWhlsFee: currentPackData.blendedWhlsFee,
          };
        }

        if (isNC && activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5) {
          setYearFormData(prev => ({
            ...prev,
            [activePackSizeTab]: {
              ...prev[activePackSizeTab],
              [activeYearTab]: finalRecalculatedData,
            },
          }));
        } else {
          setPackSizeFormData(prev => ({
            ...prev,
            [activePackSizeTab as number]: finalRecalculatedData,
          }));
        }

        const updatedFormData = {
          ...preserveGlobalFields(finalRecalculatedData),
          gsmsNetCostFromMfg: finalRecalculatedData.gsmsNetCostFromMfg,
          gsmsCostFromMfg: finalRecalculatedData.gsmsCostFromMfg,
          supplierDiscount: finalRecalculatedData.supplierDiscount,
          supplierCode: finalRecalculatedData.supplierCode,
        };

        setFormData(updatedFormData);
      } catch (error: any) {
        if (error?.is404 || error?.response?.status === 404) {
          if (isNC && activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5) {
            setYearFormData(prev => ({
              ...prev,
              [activePackSizeTab as number]: {
                ...prev[activePackSizeTab as number],
                [activeYearTab]: {
                  ...prev[activePackSizeTab as number]?.[activeYearTab],
                  supplierDiscount: 0,
                  supplierCode: supplierCode,
                },
              },
            }));
          } else {
            setPackSizeFormData(prev => ({
              ...prev,
              [activePackSizeTab as number]: {
                ...prev[activePackSizeTab as number],
                supplierDiscount: 0,
                supplierCode: supplierCode,
              },
            }));
          }
        } else {
          console.error('Error getting supplier discount:', error);
        }
      }
    };

    fetchSupplierDiscount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bidDetails?.product_type,
    bidDetails?.template_type,
    activePackSizeTab,
    activeYearTab,
    BidAnalysisStore,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    bidDetails?.template_type === 'nc' &&
    activePackSizeTab !== null &&
    activeYearTab !== null &&
    activeYearTab !== 5
      ? (yearFormData as any)[activePackSizeTab]?.[activeYearTab]?.selectedMfgNdc ||
        (yearFormData as any)[activePackSizeTab]?.[activeYearTab]?.mfg_ndc ||
        (yearFormData as any)[activePackSizeTab]?.[activeYearTab]?.gsms_mfg_ndc ||
        (yearFormData as any)[activePackSizeTab]?.[activeYearTab]?.supplierDiscount
      : (packSizeFormData as any)[activePackSizeTab as any]?.selectedMfgNdc ||
        (packSizeFormData as any)[activePackSizeTab as any]?.mfg_ndc ||
        (packSizeFormData as any)[activePackSizeTab as any]?.gsms_mfg_ndc ||
        (packSizeFormData as any)[activePackSizeTab as any]?.supplierDiscount,
  ]);

  // Load Competitive Insight and Contract Price Ranking when concept and pack size are available
  useEffect(() => {
    if (
      formData.conceptName &&
      formData.packSize &&
      bidId &&
      !isApplyingFilterRef.current
    ) {
      const bidIdNum = bidId ? parseInt(bidId) : null;
      const concept = formData.conceptName;
      const packSize = formData.packSize ? parseFloat(formData.packSize) : null;

      let shouldUseUsagePeriodDates = true;
      if (bidIdNum && concept && packSize) {
        const storedDateRange = BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize);
        if (storedDateRange !== undefined) {
          shouldUseUsagePeriodDates = false;
          if (storedDateRange.startDate && storedDateRange.endDate) {
            setChartDateRange({
              startDate: storedDateRange.startDate,
              endDate: storedDateRange.endDate,
            });
            setCalendarViewDates({
              startViewDate: storedDateRange.startDate,
              endViewDate: storedDateRange.endDate,
            });
          }
        }
      }

      if (shouldUseUsagePeriodDates) {
        const startDate = formData.usagePeriodStartDate;
        const endDate = formData.usagePeriodEndDate;

        if (startDate && endDate) {
          try {
            const parsedStart = parseDateString(startDate);
            const parsedEnd = parseDateString(endDate);

            if (
              parsedStart &&
              parsedEnd &&
              !isNaN(parsedStart.getTime()) &&
              !isNaN(parsedEnd.getTime())
            ) {
              setChartDateRange({ startDate: parsedStart, endDate: parsedEnd });
              setCalendarViewDates({ startViewDate: parsedStart, endViewDate: parsedEnd });
            }
          } catch (error) {
            console.error('Error setting chart date range from usage period:', error);
          }
        }
      }

      let datesToUse: { startDate: Date | string | null; endDate: Date | string | null } | null =
        null;

      if (bidIdNum && concept && packSize) {
        const storedDateRange = BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize);
        if (storedDateRange !== undefined && storedDateRange.startDate && storedDateRange.endDate) {
          datesToUse = {
            startDate: storedDateRange.startDate,
            endDate: storedDateRange.endDate,
          };
        }
      }

      if (!datesToUse) {
        const startDate = formData.usagePeriodStartDate;
        const endDate = formData.usagePeriodEndDate;
        if (startDate && endDate) {
          datesToUse = { startDate, endDate };
        }
      }

      // Stamp the key so the secondary usagePeriod effect below skips a redundant call.
      const loadKey = `${bidId}|${formData.conceptName}|${formData.packSize}|${formData.usagePeriodStartDate}|${formData.usagePeriodEndDate}`;
      lastMarketLoadKeyRef.current = loadKey;

      if (datesToUse && datesToUse.startDate && datesToUse.endDate) {
        const parsedStartDate =
          datesToUse.startDate instanceof Date
            ? datesToUse.startDate
            : typeof datesToUse.startDate === 'string'
              ? new Date(datesToUse.startDate)
              : null;
        const parsedEndDate =
          datesToUse.endDate instanceof Date
            ? datesToUse.endDate
            : typeof datesToUse.endDate === 'string'
              ? new Date(datesToUse.endDate)
              : null;

        if (
          parsedStartDate &&
          parsedEndDate &&
          !isNaN(parsedStartDate.getTime()) &&
          !isNaN(parsedEndDate.getTime())
        ) {
          if (parsedEndDate.getTime() < parsedStartDate.getTime()) {
            console.warn('Invalid date range: End date is before start date. Skipping graph load.');
            return;
          }

          loadMarketSalesTrendData(null, false, parsedStartDate, parsedEndDate);
          loadMarketShareByVolume(false, parsedStartDate, parsedEndDate);
          loadVolumeByCot(null, parsedStartDate, parsedEndDate);
          const startDateStr = formatDateForChartAPI(datesToUse.startDate);
          const endDateStr = formatDateForChartAPI(datesToUse.endDate);
          loadContractPriceRanking(startDateStr || undefined, endDateStr || undefined, false);
        } else {
          loadMarketSalesTrendData(null, false);
          loadMarketShareByVolume(false);
          loadVolumeByCot(null);
          loadContractPriceRanking(undefined, undefined, false);
        }
      } else {
        loadMarketSalesTrendData(null, false);
        loadMarketShareByVolume(false);
        loadVolumeByCot(null);
        loadContractPriceRanking(undefined, undefined, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.conceptName,
    formData.packSize,
    formData.usagePeriodStartDate,
    formData.usagePeriodEndDate,
    bidId,
  ]);

  // Load chart data when usage period dates are available
  useEffect(() => {
    if (isApplyingFilterRef.current) {
      return;
    }

    const currentData = (packSizeFormData as any)[activePackSizeTab as any] || formData;
    const hasUsageDates = !!(currentData.usagePeriodStartDate && currentData.usagePeriodEndDate);
    const hasRequiredFields = !!(bidId && currentData.conceptName && currentData.packSize);

    const bidIdNum = bidId ? parseInt(bidId) : null;
    const concept = currentData.conceptName;
    const packSize = currentData.packSize ? parseFloat(currentData.packSize) : null;
    const hasCustomDateFilter =
      bidIdNum && concept && packSize
        ? BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize) !== undefined
        : false;

    // Skip if the primary concept/packSize effect already fired for the exact same key —
    // prevents a double API call on initial load when both effects run in the same render cycle.
    const currentKey = `${bidId}|${formData.conceptName}|${formData.packSize}|${formData.usagePeriodStartDate}|${formData.usagePeriodEndDate}`;
    const alreadyLoadedByPrimaryEffect = lastMarketLoadKeyRef.current === currentKey;
    if (alreadyLoadedByPrimaryEffect) {
      lastMarketLoadKeyRef.current = null; // reset so subsequent user-driven changes work normally
    }

    if (hasUsageDates && !hasCustomDateFilter) {
      try {
        const parsedStart = parseDateString(currentData.usagePeriodStartDate);
        const parsedEnd = parseDateString(currentData.usagePeriodEndDate);

        if (
          parsedStart &&
          parsedEnd &&
          !isNaN(parsedStart.getTime()) &&
          !isNaN(parsedEnd.getTime())
        ) {
          if (parsedEnd.getTime() < parsedStart.getTime()) {
            setValidationErrors(prev => ({
              ...prev,
              usagePeriodEndDate: 'End date must be after start date',
            }));
            return;
          }

          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.usagePeriodEndDate;
            return newErrors;
          });

          const currentStartTime = chartDateRange.startDate?.getTime();
          const currentEndTime = chartDateRange.endDate?.getTime();
          const newStartTime = parsedStart.getTime();
          const newEndTime = parsedEnd.getTime();

          if (currentStartTime !== newStartTime || currentEndTime !== newEndTime) {
            setChartDateRange({ startDate: parsedStart, endDate: parsedEnd });
            setCalendarViewDates({ startViewDate: parsedStart, endViewDate: parsedEnd });
          }
        }
      } catch (error) {
        console.error('Error setting chart date range from usage period:', error);
      }
    }

    if (hasUsageDates && hasRequiredFields && !hasCustomDateFilter && !alreadyLoadedByPrimaryEffect) {
      const startDate = currentData.usagePeriodStartDate;
      const endDate = currentData.usagePeriodEndDate;
      if (startDate && endDate) {
        const parsedStartDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
        const parsedEndDate = typeof endDate === 'string' ? new Date(endDate) : endDate;

        if (
          parsedStartDate &&
          parsedEndDate &&
          !isNaN(parsedStartDate.getTime()) &&
          !isNaN(parsedEndDate.getTime())
        ) {
          if (parsedEndDate.getTime() < parsedStartDate.getTime()) {
            console.warn('Invalid date range: End date is before start date. Skipping graph load.');
            return;
          }
        }
        loadMarketSalesTrendData(null, false, parsedStartDate, parsedEndDate);
        loadMarketShareByVolume(false, parsedStartDate, parsedEndDate);
        loadVolumeByCot(null, parsedStartDate, parsedEndDate);
      } else {
        loadMarketSalesTrendData(null, false);
        loadMarketShareByVolume(false);
        loadVolumeByCot(null);
      }

      const startDateStr = formatDateForChartAPI(currentData.usagePeriodStartDate);
      const endDateStr = formatDateForChartAPI(currentData.usagePeriodEndDate);
      loadContractPriceRanking(startDateStr || undefined, endDateStr || undefined, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.usagePeriodStartDate,
    formData.usagePeriodEndDate,
    formData.conceptName,
    formData.packSize,
    activePackSizeTab,
    bidId,
  ]);

  // Recalculate when contract price ranking data changes
  useEffect(() => {
    if (contractPriceRanking && contractPriceRanking.length > 0) {
      const currentPackData = (packSizeFormData as any)[activePackSizeTab as any] || formData;
      const updatedData = {
        ...formData,
        ...currentPackData,
      };
      const recalculatedData = recalculateFields(updatedData);
      setPackSizeFormData(prev => ({
        ...prev,
        [activePackSizeTab as number]: recalculatedData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractPriceRanking]);

  // Set default chart date range from usage period dates
  useEffect(() => {
    const currentChartStart = chartDateRange.startDate;
    const currentChartEnd = chartDateRange.endDate;
    const chartDateRangeNotSet = !currentChartStart || !currentChartEnd;

    const currentData = (packSizeFormData as any)[activePackSizeTab as any] || formData;
    const concept = currentData.conceptName;
    const packSize = currentData.packSize ? parseFloat(currentData.packSize) : null;
    const bidIdNum = bidId ? parseInt(bidId) : null;
    const hasCustomDateFilter =
      bidIdNum && concept && packSize
        ? BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize) !== undefined
        : false;

    if (hasCustomDateFilter && !chartDateRangeNotSet) {
      return;
    }

    let usageStart = currentData.usagePeriodStartDate;
    let usageEnd = currentData.usagePeriodEndDate;

    if ((!usageStart || !usageEnd) && bidDetails?.form_details?.form_data) {
      for (const conceptItem of bidDetails.form_details.form_data) {
        if (conceptItem.pack_size) {
          for (const packSizeKey in conceptItem.pack_size) {
            const packData = conceptItem.pack_size[packSizeKey];
            if (packData.start_date || packData.end_date) {
              usageStart = usageStart || packData.start_date;
              usageEnd = usageEnd || packData.end_date;
              break;
            }
          }
          if (usageStart && usageEnd) break;
        }
      }
    }

    if (usageStart && usageEnd) {
      try {
        const startDate = parseDateString(usageStart);
        const endDate = parseDateString(usageEnd);

        if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          if (endDate.getTime() < startDate.getTime()) {
            setValidationErrors(prev => ({
              ...prev,
              usagePeriodEndDate: 'End date must be after start date',
            }));
            return;
          }

          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.usagePeriodEndDate;
            return newErrors;
          });

          const currentStart = chartDateRange.startDate;
          const currentEnd = chartDateRange.endDate;

          const currentMatchesUsagePeriod =
            currentStart &&
            currentEnd &&
            currentStart.getTime() === startDate.getTime() &&
            currentEnd.getTime() === endDate.getTime();

          const datesChanged =
            !currentStart ||
            !currentEnd ||
            currentStart.getTime() !== startDate.getTime() ||
            currentEnd.getTime() !== endDate.getTime();

          const shouldUpdate =
            chartDateRangeNotSet ||
            ((!currentStart || !currentEnd || currentMatchesUsagePeriod) && datesChanged);

          if (shouldUpdate) {
            setChartDateRange({ startDate: startDate, endDate: endDate });
            setCalendarViewDates({ startViewDate: startDate, endViewDate: endDate });
          }
        }
      } catch (error) {
        console.error('Error setting default chart date range:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.usagePeriodStartDate,
    formData.usagePeriodEndDate,
    packSizeFormData,
    activePackSizeTab,
    bidDetails,
  ]);

  // Migrate global fields from packSizeFormData to formData
  useEffect(() => {
    const globalFields = [
      'analysisDate',
      'targetLaunchDate',
      'contracts',
      'contractsData',
      'supplierName',
      'analysisType',
    ];
    let needsFormDataUpdate = false;
    let needsPackSizeCleanup = false;
    const updates: any = {};
    const cleanedPackSizeData: any = {};

    Object.keys(packSizeFormData).forEach(tabIndex => {
      const tabData = packSizeFormData[parseInt(tabIndex, 10) as number];
      if (tabData) {
        const hasGlobalFields = globalFields.some(
          field => tabData[field] !== undefined && tabData[field] !== null && tabData[field] !== ''
        );

        if (hasGlobalFields) {
          const {
            contracts: _contracts,
            contractsData: _contractsData,
            analysisDate: _analysisDate,
            targetLaunchDate: _targetLaunchDate,
            supplierName: _supplierName,
            analysisType: _analysisType,
            ...rest
          } = tabData;
          cleanedPackSizeData[parseInt(tabIndex, 10) as number] = rest;
          needsPackSizeCleanup = true;
        }

        globalFields.forEach(field => {
          if (
            tabData[field] !== undefined &&
            tabData[field] !== null &&
            tabData[field] !== '' &&
            (!formData[field] || formData[field] === null || formData[field] === '')
          ) {
            updates[field] = tabData[field];
            needsFormDataUpdate = true;
          }
        });
      }
    });

    if (needsFormDataUpdate) {
      setFormData((prev: any) => ({ ...prev, ...updates }));
    }

    if (needsPackSizeCleanup) {
      setPackSizeFormData((prev: any) => {
        const updated: any = { ...prev };
        Object.keys(cleanedPackSizeData).forEach(tabIndex => {
          const idx = parseInt(tabIndex, 10) as number;
          updated[idx] = cleanedPackSizeData[idx];
        });
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    packSizeFormData,
    formData.analysisDate,
    formData.targetLaunchDate,
    formData.contracts,
    formData.supplierName,
    formData.analysisType,
  ]);

  // Populate contractsData when contractsList is loaded
  useEffect(() => {
    const currentBidDetails = BidAnalysisStore.currentBidDetails;

    if (contractsExplicitlyClearedRef.current) {
      if (formData.contracts && formData.contracts.trim() !== '') {
        contractsExplicitlyClearedRef.current = false;
      } else {
        return;
      }
    }

    if (formData.contracts === '') {
      return;
    }

    if (formData.contractsData !== undefined && formData.contractsData !== null) {
      return;
    }

    const contractsString = formData.contracts || currentBidDetails?.intended_contracts;

    if (
      contractsList.length > 0 &&
      contractsString &&
      typeof contractsString === 'string' &&
      contractsString.trim()
    ) {
      const contractNames = contractsString.split(',').map((name: string) => name.trim());
      const matchedContracts = contractsList.filter(c => contractNames.includes(c.contract_name));
      if (matchedContracts.length > 0) {
        setFormData((prev: any) => ({
          ...prev,
          contracts: prev.contracts || contractsString,
          contractsData: matchedContracts,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsList, formData.contracts]);

  // Validate target launch date
  const targetLaunchDateStr = getDateString(formData.targetLaunchDate);
  const analysisDateStr = getDateString(formData.analysisDate);

  useEffect(() => {
    if (formData.targetLaunchDate && formData.analysisDate) {
      const error = validateTargetLaunchDate(formData.targetLaunchDate, formData.analysisDate);
      if (error) {
        setValidationErrors(prev => ({
          ...prev,
          targetLaunchDate: error,
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.targetLaunchDate;
          return newErrors;
        });
      }
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.targetLaunchDate;
        return newErrors;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetLaunchDateStr, analysisDateStr]);

  // Validate suggestedBidPrice
  useEffect(() => {
    const currentData = (packSizeFormData as any)[activePackSizeTab as any] || formData;
    const suggestedBidPrice = currentData.suggestedBidPrice;

    if (suggestedBidPrice !== null && suggestedBidPrice !== undefined) {
      const error = validateField('suggestedBidPrice', suggestedBidPrice);
      if (error) {
        setValidationErrors(prev => ({
          ...prev,
          suggestedBidPrice: error,
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.suggestedBidPrice;
          return newErrors;
        });
      }
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.suggestedBidPrice;
        return newErrors;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.suggestedBidPrice, packSizeFormData, activePackSizeTab, analysisType]);

  // Sync gsmsHndlgType state with formData
  useEffect(() => {
    setFormData((prev: any) => {
      if (prev.gsmsHndlgType !== gsmsHndlgType) {
        return {
          ...prev,
          gsmsHndlgType: gsmsHndlgType,
        };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsmsHndlgType]);

  // Sync formData.gsmsHndlgType back to gsmsHndlgType state
  useEffect(() => {
    if (
      formData.gsmsHndlgType &&
      formData.gsmsHndlgType !== gsmsHndlgType &&
      !gsmsHndlgTypeManuallyChangedRef.current
    ) {
      setGsmsHndlgType(formData.gsmsHndlgType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.gsmsHndlgType]);

  // Re-sync gsmsHndlgType from bidDetails after handlingTypesList loads
  useEffect(() => {
    const bidDetailsCurrent = BidAnalysisStore.currentBidDetails;
    if (bidDetailsCurrent?.gsms_handling_type && handlingTypesList.length > 0) {
      if (
        gsmsHndlgType !== bidDetailsCurrent.gsms_handling_type &&
        !gsmsHndlgTypeManuallyChangedRef.current
      ) {
        setGsmsHndlgType(bidDetailsCurrent.gsms_handling_type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlingTypesList, BidAnalysisStore.currentBidDetails?.gsms_handling_type, gsmsHndlgType]);

  // Load NDC options when supplier, selected concept, or packsize changes
  useEffect(() => {
    const conceptName = selectedConcept || formData.conceptName;
    const packSizeFromTab = activePackSizeTab !== null
      ? (packSizeFormData as any)[activePackSizeTab as any]?.packSize
      : undefined;
    const packSize = packSizeFromTab || (formData as any).packSize;

    if (supplierName && conceptName && packSize) {
      loadNdcOptions(supplierName, conceptName, parseFloat(packSize));
    } else {
      setNdcOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    supplierName,
    selectedConcept,
    formData.conceptName,
    formData.packSize,
    activePackSizeTab,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    activePackSizeTab !== null ? (packSizeFormData as any)[activePackSizeTab]?.packSize : undefined,
    bidDetails?.template_type,
  ]);

  // Prevent calendar trigger buttons from causing page scroll
  useEffect(() => {
    const handleCalendarButtons = () => {
      const calendarTriggers = document.querySelectorAll(
        '.p-calendar-trigger button, .chart-date-picker .p-calendar-trigger button'
      );
      calendarTriggers.forEach(button => {
        if (button instanceof HTMLButtonElement) {
          button.type = 'button';
        } else {
          button.setAttribute('type', 'button');
        }
      });
    };

    handleCalendarButtons();

    const observer = new MutationObserver(() => {
      setTimeout(handleCalendarButtons, 0);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [showChartModal, selectedCompany]);

  // Mark initial load as complete
  useEffect(() => {
    if (!loading && bidId) {
      const timer = setTimeout(() => {
        if (!hasUserMadeChangesRef.current) {
          isInitialLoadRef.current = false;
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, bidId]);

  // Always restrict navigation when on this page
  useEffect(() => {
    setisNavigationRestricted(true);
    return () => {
      setisNavigationRestricted(false);
      setIsNavigatingAway(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setisNavigationRestricted]);

  // Intercept browser back button
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      if (isNavigatingAway) {
        return;
      }

      const currentPath = location.pathname;
      const isOnTemplatePage = currentPath.includes('/bdportal/bid-analysis/template/');

      if (isOnTemplatePage) {
        window.history.pushState(null, '', currentPath);
        setIsBackNavigation(true);
        const backPath = isManagerReviewMode
          ? '/bdportal/bid-analysis/manager-review'
          : '/bdportal/bid-analysis/dashboard';
        setPendingNavigation(backPath);
      }
    };

    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isNavigatingAway, isManagerReviewMode]);
}
